import React from 'react'

import { Color, Link, LinkProps, LinkVariant, withCitrusTheme } from '@gousto-internal/citrus-react'

export type CWCLinkButtonVariant = 'primary' | 'secondary'

export type CWCLinkButtonColorMode = 'light' | 'dark'

export type CWCLinkButtonProps = {
  href: string
  children: React.ReactNode
  /**
   * Receives the click event so signup-bound CTAs can `preventDefault()` the raw anchor
   * navigation and dispatch the promo/redirect flow instead. CTAs to other pages can ignore
   * the event and just navigate via `href` while tracking the click.
   */
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void
  /**
   * `primary` (default) = solid purple fill. `secondary` = transparent fill with a 2px purple
   * outline and purple text.
   */
  variant?: CWCLinkButtonVariant
  /**
   * Passed through to the underlying anchor — lets a caller pull this CTA out of (or back into)
   * the tab order (e.g. CWCFloatingCTA while it's visually hidden) without a new component.
   */
  tabIndex?: number
  /**
   * `light` (default) = the shared `Secondary_400`/`Secondary_50`/`Secondary_600` tokens, for a
   * button on a light background. `dark` = for a button on a dark background — Citrus doesn't
   * have dark-mode button tokens yet (confirmed with design), so these are this component's own
   * values until Citrus ships proper ones: rest `#9C99FF`, hover text `#7D86FE` / border
   * `#A29EFF`, plus a white 16%-opacity overlay wash on hover (not a solid fill change).
   */
  colorMode?: CWCLinkButtonColorMode
  /** Responsive font-size override, in `theme.responsiveStyle` array form. */
  fontSizeOverride?: [string, string, string, string]
  /** Responsive max-width cap, in `theme.responsiveStyle` array form. */
  maxWidthOverride?: [string, string, string, string]
}

/**
 * A navigational CTA that looks like a Citrus button but is a real anchor (`<a href>`) under the
 * hood — Citrus has no link-styled-as-button primitive, and elements that navigate to another
 * page should be links, not buttons. The visual variant travels as `buttonVariant`, stripped
 * before it reaches the DOM (mirrors `InlineLink`), so it never collides with Citrus `Link`'s own
 * `variant` prop.
 */
// `rest` has no matching Citrus token (confirmed against the installed package — not just assumed).
// The hover colours do: #a29eff/#7d86fe are Secondary_200/Secondary_300, sourced via theme.color
// below rather than hardcoded here.
const DARK_MODE_COLOR = {
  rest: '#9c99ff',
  hoverOverlay: 'rgba(255, 255, 255, 0.16)',
}

type StyledLinkProps = LinkProps & {
  buttonVariant?: CWCLinkButtonVariant
  colorMode?: CWCLinkButtonColorMode
  fontSizeOverride?: [string, string, string, string]
  maxWidthOverride?: [string, string, string, string]
}

const StyledLink = withCitrusTheme<StyledLinkProps>(
  (props: StyledLinkProps) => {
    const { buttonVariant, colorMode, fontSizeOverride, maxWidthOverride, ...linkProps } = props

    return <Link {...linkProps} />
  },
  (theme, props: StyledLinkProps) => {
    const isSecondary = props.buttonVariant === 'secondary'
    const isDark = props.colorMode === 'dark'
    const secondaryColor = isDark ? DARK_MODE_COLOR.rest : theme.color(Color.Secondary_400)

    return [
      {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        minHeight: '48px',
        padding: '10px 24px',
        borderRadius: '8px',
        fontFamily: theme.tokens.FontFamilies.bold,
        textDecoration: 'none',
        // 16px constant. Citrus Link with variant=None renders bare children (no Link1 wrapper), so
        // its `size` prop is ignored and the text would otherwise inherit the theme's ramping body
        // default (16→18 at >=768). A base fontSize on the anchor overrides that inherited value at
        // every breakpoint — and there's no competing same-element Citrus media rule to lose to.
        fontSize: '16px',
        backgroundColor: isSecondary ? 'transparent' : theme.color(Color.Secondary_400),
        color: isSecondary ? secondaryColor : theme.color(Color.White),
        border: isSecondary ? `2px solid ${secondaryColor}` : 'none',
        // Dark mode's hover is a translucent white wash bleeding past the border, not a solid
        // fill — a transparent-at-rest pseudo-element that `&:hover::after` colours in.
        '&::after': isDark
          ? {
              content: '""',
              position: 'absolute',
              inset: '-2px',
              borderRadius: '8px',
              backgroundColor: 'transparent',
              pointerEvents: 'none',
            }
          : undefined,
        ':hover': isDark
          ? {
              borderColor: theme.color(Color.Secondary_200),
              color: theme.color(Color.Secondary_300),
            }
          : {
              backgroundColor: isSecondary
                ? theme.color(Color.Secondary_50)
                : theme.color(Color.Secondary_600),
              color: isSecondary ? theme.color(Color.Secondary_600) : theme.color(Color.White),
            },
        '&:hover::after': isDark ? { backgroundColor: DARK_MODE_COLOR.hoverOverlay } : undefined,
        ':focus-visible': {
          outline: `1px solid ${theme.color(Color.Secondary_600)}`,
          outlineOffset: '2px',
        },
      },
      theme.responsiveStyle(['100%', null, '345px'], ['width']),
      // Secondary is 16px/24px per ui-design — Citrus's FontSize scale index 4 pairs exactly this
      // fontSize/lineHeight, so this sources both from the token table rather than hardcoding
      // either as a raw value. Primary keeps the inherited Link size.
      isSecondary ? theme.responsiveFont(4) : undefined,
      props.fontSizeOverride
        ? theme.responsiveStyle(props.fontSizeOverride, ['fontSize'])
        : undefined,
      props.maxWidthOverride
        ? theme.responsiveStyle(props.maxWidthOverride, ['maxWidth'])
        : undefined,
    ]
  },
)

export const CWCLinkButton = ({
  href,
  children,
  onClick,
  variant = 'primary',
  tabIndex,
  colorMode,
  fontSizeOverride,
  maxWidthOverride,
}: CWCLinkButtonProps) => {
  // A real <a href> is already keyboard-accessible: it's in the tab order (Tab) and activates on
  // Enter natively. Anchors do NOT activate on Space (Space scrolls the page), so — because this link
  // is styled as a button — add Space activation for button-consistent keyboard operability. Arrow
  // keys keep their default (page scroll); they are not activation keys for a link or a button.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLAnchorElement>) => {
    if (event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault()
      event.currentTarget.click()
    }
  }

  return (
    <StyledLink
      href={href}
      variant={LinkVariant.None}
      buttonVariant={variant}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      tabIndex={tabIndex}
      colorMode={colorMode}
      fontSizeOverride={fontSizeOverride}
      maxWidthOverride={maxWidthOverride}
    >
      {children}
    </StyledLink>
  )
}
